<script setup lang="ts">
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import V2CatalogActionList from '@/components/v2/catalog/panels/V2CatalogActionList.vue';
  import type { ICatalogMenuEntry } from '@/domain/catalog/catalog-menus';

  /**
   * The editor's header (plan §5.12): the name and a subtitle, the ⋯ with
   * the rarer actions, and ✕ (the docked editor has no sheet ✕ of its own).
   */
  withDefaults(
    defineProps<{
      title: string;
      subtitle?: string;
      entries?: readonly ICatalogMenuEntry<string>[];
      online: boolean;
      /** Its own ✕: only when docked (the sheet has one). */
      showClose?: boolean;
    }>(),
    { subtitle: undefined, entries: () => [], showClose: true }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'select', key: string): void;
  }>();

  function choose(key: string, close: () => void) {
    close();
    emit('select', key);
  }
</script>

<template>
  <header class="v2-catalog-editor-header">
    <div class="header-text">
      <h2 class="header-title" :title="title">{{ title }}</h2>
      <p v-if="subtitle" class="header-subtitle" :title="subtitle">
        {{ subtitle }}
      </p>
    </div>
    <PosUiPopover
      v-if="entries.length"
      class="header-more"
      placement="bottom"
      align="end"
    >
      <template #trigger="{ toggle }">
        <PosUiButton
          color="neutral"
          variant="ghost"
          icon="dots-horizontal"
          :aria-label="$t('menuPage.more.title')"
          :title="$t('menuPage.more.title')"
          @click="toggle"
        />
      </template>
      <template #default="{ close }">
        <V2CatalogActionList
          class="more-list"
          :entries="entries"
          :online="online"
          @select="choose($event, close)"
        />
      </template>
    </PosUiPopover>
    <PosUiButton
      v-if="showClose"
      class="header-close"
      color="neutral"
      variant="ghost"
      icon="close"
      :aria-label="$t('close')"
      :title="$t('close')"
      @click="emit('close')"
    />
  </header>
</template>

<style scoped lang="scss">
  .v2-catalog-editor-header {
    @apply tw-flex tw-flex-none tw-items-start tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-surface tw-py-2 tw-pl-4 tw-pr-2;

    .header-text {
      @apply tw-min-w-0 tw-flex-1 tw-py-1.5;
    }

    .header-title {
      @apply tw-m-0 tw-truncate tw-text-base tw-font-semibold tw-leading-6 tw-text-neutral-900;
    }

    .header-subtitle {
      @apply tw-m-0 tw-truncate tw-text-[13px] tw-leading-5 tw-text-neutral-500;
    }

    .header-more {
      @apply tw-ml-1 tw-flex-none;
    }

    .more-list {
      width: 230px;
    }

    .header-close {
      @apply tw-flex-none;
    }
  }
</style>
